import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import {
	apiPaths,
	revolutAccountsSchema,
	revolutAccountTransactionsPath,
	revolutAuthorizationSchema,
	revolutConnectionSchema,
	revolutPaymentsSchema,
	revolutTransactionsSchema,
	type CompleteRevolutConnectionRequest,
	type RevolutAuthorization,
	type RevolutConnection,
	type RevolutPayments,
} from "@ravnar/api-contract";

import { apiRequest } from "./client";
import { LIST_STALE_TIME_MS } from "./refresh";

// Revolut's requests. The connection is a setting, so its key starts with "settings" and a save anywhere in the
// settings refreshes it too — and so are payments, under the connection's own key, which asking the connection again
// asks again too; the accounts are Revolut's own data, read from Revolut on every ask, under "revolut".

/** Whether the organization is connected to Revolut, and who connected it. Only owners and admins may read it. */
export const revolutConnectionQueryOptions = () =>
	queryOptions({
		queryKey: ["settings", "revolut"],
		queryFn: ({ signal }) => apiRequest(apiPaths.revolutConnection, revolutConnectionSchema, { signal }),
		staleTime: LIST_STALE_TIME_MS,
	});

/**
 * How long the accounts read from Revolut count as fresh. Every ask is a round of requests to Revolut, one per account
 * for its balance, so a page opened again within the minute shows what it had rather than asking again.
 */
export const REVOLUT_ACCOUNTS_STALE_TIME_MS = 60_000;

/** The connected accounts with their balances, read from Revolut; anyone who can read Finance. */
export const revolutAccountsQueryOptions = () =>
	queryOptions({
		queryKey: ["revolut", "accounts"],
		queryFn: ({ signal }) => apiRequest(apiPaths.revolutAccounts, revolutAccountsSchema, { signal }),
		staleTime: REVOLUT_ACCOUNTS_STALE_TIME_MS,
	});

/**
 * How long an account's transactions read from Revolut count as fresh. Revolut lets an app read an account's
 * transactions no more than four times a day unless a person is asking for them — a person is, here, but a drawer
 * opened again within the minute shows what it had rather than asking again.
 */
export const REVOLUT_TRANSACTIONS_STALE_TIME_MS = 60_000;

/** One account's latest transactions, newest first, read from Revolut; anyone who can read Finance. */
export const revolutTransactionsQueryOptions = (accountId: string) =>
	queryOptions({
		queryKey: ["revolut", "transactions", accountId],
		queryFn: ({ signal }) =>
			apiRequest(revolutAccountTransactionsPath(accountId), revolutTransactionsSchema, { signal }),
		staleTime: REVOLUT_TRANSACTIONS_STALE_TIME_MS,
	});

/** Starts connecting, and answers where to send the browser for Revolut to ask for approval. */
export function startRevolutConnection(): Promise<RevolutAuthorization> {
	return apiRequest(apiPaths.revolutConnection, revolutAuthorizationSchema, { method: "POST" });
}

/**
 * Finishes connecting with what Revolut sent the browser back with. Fails with 409 when the approval is not the one this
 * member started, took too long or was used already.
 */
export function completeRevolutConnection(request: CompleteRevolutConnectionRequest): Promise<RevolutConnection> {
	return apiRequest(apiPaths.revolutConnectionAuthorization, revolutConnectionSchema, {
		method: "POST",
		body: request,
	});
}

/**
 * Whether Finance may send bills to Revolut as draft payments, and who allowed it. Only owners and admins may read it.
 */
export const revolutPaymentsQueryOptions = () =>
	queryOptions({
		queryKey: ["settings", "revolut", "payments"],
		queryFn: ({ signal }) => apiRequest(apiPaths.revolutPayments, revolutPaymentsSchema, { signal }),
		staleTime: LIST_STALE_TIME_MS,
	});

/** Starts allowing payments, and answers where to send the browser for Revolut Business to ask for approval. */
export function startRevolutPayments(): Promise<RevolutAuthorization> {
	return apiRequest(apiPaths.revolutPayments, revolutAuthorizationSchema, { method: "POST" });
}

/**
 * Finishes allowing payments with what Revolut sent the browser back with. Fails with 409 when the approval is not the
 * one this member started, took too long or was used already.
 */
export function completeRevolutPayments(request: CompleteRevolutConnectionRequest): Promise<RevolutPayments> {
	return apiRequest(apiPaths.revolutPaymentsAuthorization, revolutPaymentsSchema, { method: "POST", body: request });
}

/** Stops payments: the API forgets the tokens that create drafts in Revolut. */
export async function stopRevolutPayments(): Promise<void> {
	await apiRequest(apiPaths.revolutPayments, z.undefined(), { method: "DELETE" });
}

/** Disconnects Revolut: the API forgets its access and asks Revolut to withdraw it. */
export async function disconnectRevolut(): Promise<void> {
	await apiRequest(apiPaths.revolutConnection, z.undefined(), { method: "DELETE" });
}
